import { useEffect } from 'react'
import { create } from 'zustand'
import { errorMessage, req, type IntegrationsStatus } from '../../lib/api'
import { useStore } from '../../lib/store'
import type { Reach } from './reach'
import { t } from '@/lib/i18n'

export type HeatmapKind = 'click' | 'rageclick'

export interface HeatmapData {
  page: string
  width: number
  days: number
  type: HeatmapKind
  /** density in the frame's own pixels */
  points: { x: number; y: number; n: number }[]
  reach: Reach
}

type Entry = { kind: HeatmapKind; data?: HeatmapData }

interface HeatmapState {
  /** frames painting a heatmap, by frame id; `data` is unset while it loads */
  frames: Record<string, Entry>
  /** whether this person has PostHog connected; undefined until asked */
  connected?: boolean
}

/** Which frames wear a PostHog heatmap. Per browser tab and session: it is a
 *  lens one person looks through, never part of the design. */
export const useHeatmaps = create<HeatmapState>(() => ({ frames: {} }))

let asked = false
/** bumped whenever the connection changes, so a heatmap that was loading
 *  under the old one is dropped when it lands */
let generation = 0

/** Record a connect, project change or disconnect from the Integrations
 *  page, which shares this tab's state with the canvas: the frame menu
 *  follows at once, and the heatmaps the old connection painted come down. */
export function setPostHogConnected(connected: boolean) {
  asked = true
  generation++
  /* a new project (or none) makes every painted heatmap someone else's data */
  useHeatmaps.setState({ connected, frames: {} })
}

/** Whether the frame menu should offer heatmaps: asked once per page load,
 *  then kept current by `setPostHogConnected`. Signed-out viewers get false. */
export function usePostHogConnected(): boolean {
  const connected = useHeatmaps((s) => s.connected)
  useEffect(() => {
    if (asked) return
    asked = true
    req<IntegrationsStatus>('/api/integrations')
      .then((status) => useHeatmaps.setState({ connected: !!(status.posthog as { connected?: boolean })?.connected }))
      .catch(() => useHeatmaps.setState({ connected: false }))
  }, [])
  return connected ?? false
}

export function hideHeatmap(frameId: string) {
  useHeatmaps.setState((s) => {
    const { [frameId]: _gone, ...rest } = s.frames
    return { frames: rest }
  })
}

export async function showHeatmap(canvasId: string, frameId: string, kind: HeatmapKind) {
  const started = generation
  useHeatmaps.setState((s) => ({ frames: { ...s.frames, [frameId]: { kind } } }))
  try {
    const data = await req<HeatmapData>(
      `/api/canvases/${encodeURIComponent(canvasId)}/posthog/heatmap?frameId=${encodeURIComponent(frameId)}&type=${kind}`,
    )
    /* a later pick, a Hide or a connection change while this one loaded wins */
    if (started !== generation || useHeatmaps.getState().frames[frameId]?.kind !== kind) return
    useHeatmaps.setState((s) => ({ frames: { ...s.frames, [frameId]: { kind, data } } }))
    if (data.points.length === 0) {
      useStore.getState().pushNotice(
        t(kind === 'click' ? 'stage.posthogNoClicks' : 'stage.posthogNoRageClicks', {
          page: data.page,
          days: data.days,
        }),
      )
    }
  } catch (err) {
    if (started !== generation) return
    if (useHeatmaps.getState().frames[frameId]?.kind === kind) hideHeatmap(frameId)
    useStore.getState().pushNotice(errorMessage(err, t('stage.posthogHeatmapFailed')))
  }
}
